import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Download, Copy, KeySquare, Chrome, Check } from "lucide-react";
import { toast } from "sonner";
import { API_BASE } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function ChromeExtensionPanel({ open, onOpenChange }) {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);

  const copy = async (text) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success("Copied");
  };

  const downloadUrl = `${API_BASE}/chrome-extension/download`;
  const apiBase = API_BASE.replace(/\/api\/?$/, "");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="chrome-ext-modal" className="max-w-xl bg-charcoal border-border">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-white flex items-center gap-2">
            <Chrome className="h-4 w-4 text-teal" /> SaiResponse for Gmail & Slack
          </DialogTitle>
          <DialogDescription className="text-white/50">
            One-click drafting from inside Gmail and Slack composers — calls your private API key, never stores your text.
          </DialogDescription>
        </DialogHeader>

        <ol className="mt-2 space-y-3 text-sm text-white/80">
          <li className="flex gap-3">
            <span className="font-mono text-teal text-[11px] mt-0.5">01</span>
            <div>
              <div className="text-white font-medium">Download the extension</div>
              <a
                data-testid="chrome-ext-download"
                href={downloadUrl}
                className="inline-flex items-center gap-1.5 mt-1.5 text-sm text-teal hover:text-white border border-teal/40 hover:border-teal bg-teal/10 px-3 py-1.5 rounded-sm"
              >
                <Download className="h-3.5 w-3.5" /> sairesponse-extension.zip
              </a>
              <p className="text-xs text-white/45 mt-1">Unzip it anywhere on your machine.</p>
            </div>
          </li>

          <li className="flex gap-3">
            <span className="font-mono text-teal text-[11px] mt-0.5">02</span>
            <div>
              <div className="text-white font-medium">Open <code className="text-teal font-mono text-xs">chrome://extensions</code></div>
              <p className="text-xs text-white/55 mt-1">Toggle on <b>Developer mode</b> in the top-right, then click <b>Load unpacked</b> and select the unzipped <code className="text-teal font-mono text-xs">chrome_extension</code> folder.</p>
            </div>
          </li>

          <li className="flex gap-3">
            <span className="font-mono text-teal text-[11px] mt-0.5">03</span>
            <div className="flex-1 min-w-0">
              <div className="text-white font-medium">Click the SaiResponse icon in your toolbar, then paste:</div>
              <div className="mt-2 space-y-2">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-white/40 mb-1">API Base</div>
                  <div className="flex items-stretch gap-2">
                    <code className="flex-1 bg-charcoal border border-border px-3 py-2 rounded-sm text-xs font-mono text-teal break-all" data-testid="chrome-ext-api-base">
                      {apiBase}
                    </code>
                    <Button size="sm" variant="outline" onClick={() => copy(apiBase)} className="h-auto border-border bg-charcoal text-white/70 rounded-sm">
                      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    </Button>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-white/40 mb-1 flex items-center gap-1.5">
                    <KeySquare className="h-3 w-3" /> Your API Key
                  </div>
                  <div className="flex items-stretch gap-2">
                    <code className="flex-1 bg-charcoal border border-border px-3 py-2 rounded-sm text-xs font-mono text-teal break-all" data-testid="chrome-ext-api-key">
                      {user?.api_key || "—"}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="chrome-ext-copy-key"
                      onClick={() => user?.api_key && copy(user.api_key)}
                      disabled={!user?.api_key}
                      className="h-auto border-border bg-charcoal text-white/70 rounded-sm"
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </li>

          <li className="flex gap-3">
            <span className="font-mono text-teal text-[11px] mt-0.5">04</span>
            <div>
              <div className="text-white font-medium">Open Gmail or Slack</div>
              <p className="text-xs text-white/55 mt-1">A <b>SaiResponse</b> button appears in every reply/composer toolbar. Click it and your response streams directly into the reply field.</p>
            </div>
          </li>
        </ol>
      </DialogContent>
    </Dialog>
  );
}
